/**
 * protein78 ana sayfa davranışları (cd-* ana sayfa bölümleri).
 * Modül olarak yüklenir (type="module" → ertelenmiş, aynı URL bir kez çalışır).
 *
 * - <cdh-tabbed-shelf>  sekmeli raf: sekme değişimi (tıklama, ok/Home/End), kaydırma okları, "Tümünü gör" güncelleme;
 *                       gizli sekmenin kartları <template data-raf-sablon> içinde gelir, sekme ilk açılınca klonlanır
 * - <cdh-scroller>      yatay iz için önceki/sonraki okları (Google yorumları)
 * - <cdh-bundle-card>   "Paketi sepete ekle": ürünlerin varyantlarını tek Cart API isteğiyle ekler,
 *                       Horizon sepet çekmecesi/ikonunu standart CartLinesUpdateEvent ile günceller
 * - [data-cdh-saatler]  mağaza saatlerinde bugünün satırını İstanbul saatine göre vurgular
 */
import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const azHareket = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Yatay kaydırmalı bir izin ok düğmelerini yönetir.
 * @param {HTMLElement | null} iz
 * @param {HTMLElement | null} oklar - [data-oklar] kapsayıcısı (içinde 2 buton)
 * @param {AbortSignal} signal
 */
function okBagla(iz, oklar, signal) {
  if (!iz || !oklar) return;
  const [geri, ileri] = oklar.querySelectorAll('button');

  const guncelle = () => {
    const tasma = iz.scrollWidth - iz.clientWidth > 2;
    oklar.hidden = !tasma;
    if (!tasma) return;
    const sol = Math.abs(iz.scrollLeft);
    if (geri) geri.disabled = sol <= 2;
    if (ileri) ileri.disabled = sol >= iz.scrollWidth - iz.clientWidth - 2;
  };

  iz.addEventListener('scroll', guncelle, { passive: true, signal });
  const gozlemci = new ResizeObserver(guncelle);
  gozlemci.observe(iz);
  signal.addEventListener('abort', () => gozlemci.disconnect(), { once: true });
  guncelle();
}

/**
 * @param {HTMLElement | null} iz
 * @param {number} yon - -1 geri, 1 ileri
 */
function kaydir(iz, yon) {
  if (!iz) return;
  const adim = Math.max(iz.clientWidth * 0.9, 200);
  iz.scrollBy({ left: yon * adim, behavior: azHareket() ? 'auto' : 'smooth' });
}

/**
 * Sekmeli ürün rafı.
 * @extends {Component}
 */
class CdhTabbedShelf extends Component {
  /** @type {AbortController | null} */
  #panelAc = null;

  connectedCallback() {
    super.connectedCallback();
    this.#panelBagla();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#panelAc?.abort();
  }

  /** @returns {HTMLElement[]} */
  get sekmeler() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  /** @returns {HTMLElement | null} */
  get aktifPanel() {
    return this.querySelector('.cdh-raf__panel:not([hidden])');
  }

  /** @param {Event} event */
  select(event) {
    const sekme = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (sekme instanceof HTMLElement) this.#etkinlestir(sekme, false);
  }

  /** @param {KeyboardEvent} event */
  onTabKeydown(event) {
    const sekmeler = this.sekmeler;
    const simdiki = sekmeler.findIndex((s) => s.getAttribute('aria-selected') === 'true');
    let hedef = -1;
    if (event.key === 'ArrowRight') hedef = (simdiki + 1) % sekmeler.length;
    else if (event.key === 'ArrowLeft') hedef = (simdiki - 1 + sekmeler.length) % sekmeler.length;
    else if (event.key === 'Home') hedef = 0;
    else if (event.key === 'End') hedef = sekmeler.length - 1;
    if (hedef < 0) return;
    event.preventDefault();
    const sekme = sekmeler[hedef];
    if (sekme) this.#etkinlestir(sekme, true);
  }

  scrollPrev() {
    kaydir(this.aktifPanel?.querySelector('[data-raf-iz]') ?? null, -1);
  }

  scrollNext() {
    kaydir(this.aktifPanel?.querySelector('[data-raf-iz]') ?? null, 1);
  }

  /**
   * @param {HTMLElement} sekme
   * @param {boolean} odakla
   */
  #etkinlestir(sekme, odakla) {
    const panelId = sekme.getAttribute('aria-controls');
    const hedef = panelId ? document.getElementById(panelId) : null;
    if (hedef && this.contains(hedef)) this.#paneliDoldur(hedef);
    for (const s of this.sekmeler) {
      const secili = s === sekme;
      s.setAttribute('aria-selected', String(secili));
      s.tabIndex = secili ? 0 : -1;
    }
    for (const panel of this.querySelectorAll('.cdh-raf__panel')) {
      if (panel instanceof HTMLElement) panel.hidden = panel.id !== panelId;
    }
    if (odakla) {
      sekme.focus();
      sekme.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: azHareket() ? 'auto' : 'smooth' });
    }

    const panel = this.aktifPanel;
    const tum = this.querySelector('[data-tum]');
    if (panel && tum instanceof HTMLAnchorElement) {
      const link = panel.dataset.tumLink;
      const metin = panel.dataset.tumMetin;
      if (link) tum.href = link;
      const metinEl = tum.querySelector('[data-tum-metin]');
      if (metin && metinEl) metinEl.textContent = metin;
      tum.hidden = !metin;
    }
    this.#panelBagla();
  }

  /**
   * Gizli sekmenin kartları ilk yüklemede DOM'a girmez (görsel isteği, hidrasyon yok).
   * Sekme ilk açıldığında şablon klonlanıp yerine konur; sonraki açılışlarda şablon kalmadığı için bir şey yapmaz.
   * Panelin SEO için bıraktığı gizli koleksiyon bağlantısı (data-raf-seo) kartlar gelince kaldırılır.
   * @param {HTMLElement} panel
   */
  #paneliDoldur(panel) {
    const sablon = panel.querySelector(':scope > template[data-raf-sablon]');
    if (!(sablon instanceof HTMLTemplateElement)) return;
    panel.querySelector(':scope > [data-raf-seo]')?.remove();
    sablon.replaceWith(document.importNode(sablon.content, true));
  }

  #panelBagla() {
    this.#panelAc?.abort();
    this.#panelAc = new AbortController();
    const iz = this.aktifPanel?.querySelector('[data-raf-iz]');
    okBagla(iz instanceof HTMLElement ? iz : null, this.querySelector('[data-oklar]'), this.#panelAc.signal);
  }
}

/**
 * Tek izli kaydırıcı (Google yorumları).
 * @extends {Component}
 */
class CdhScroller extends Component {
  /** @type {AbortController | null} */
  #ac = null;

  connectedCallback() {
    super.connectedCallback();
    this.#ac?.abort();
    this.#ac = new AbortController();
    const iz = this.querySelector('[data-raf-iz]');
    okBagla(iz instanceof HTMLElement ? iz : null, this.querySelector('[data-oklar]'), this.#ac.signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ac?.abort();
  }

  get iz() {
    const iz = this.querySelector('[data-raf-iz]');
    return iz instanceof HTMLElement ? iz : null;
  }

  scrollPrev() {
    kaydir(this.iz, -1);
  }

  scrollNext() {
    kaydir(this.iz, 1);
  }
}

/**
 * Paket kartı: "Paketi sepete ekle".
 * @extends {Component}
 */
class CdhBundleCard extends Component {
  /** @type {AbortController | null} */
  #istek = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#istek?.abort();
  }

  /** @param {Event} event */
  async addBundle(event) {
    const buton = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(buton instanceof HTMLButtonElement) || buton.getAttribute('aria-busy') === 'true') return;

    const varyantlar = (this.dataset.varyantlar || '').split(',').filter(Boolean);
    if (!varyantlar.length) return;

    const durum = this.querySelector('[data-durum]');
    const metin = buton.dataset.metin || buton.textContent || '';
    const items = varyantlar.map((id) => ({ id: Number(id), quantity: 1 }));

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const routes = typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : null;
    const ekleUrl = routes?.cart_add_url || '/cart/add.js';
    const sepetUrl = `${routes?.cart_url || '/cart'}.js`;

    buton.setAttribute('aria-busy', 'true');
    buton.disabled = true;

    const ertelenen = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: ertelenen.promise,
      })
    );

    this.#istek?.abort();
    this.#istek = new AbortController();
    const { signal } = this.#istek;

    try {
      const yanit = await fetch(ekleUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
        signal,
      });
      const veri = await yanit.json();
      const sepet = await fetch(sepetUrl, { headers: { Accept: 'application/json' }, signal }).then((r) => r.json());
      const hata = !yanit.ok || Boolean(veri?.status);

      ertelenen.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(sepet),
        detail: {
          items: sepet.items,
          source: 'cdh-bundle-card',
          sourceId: this.id || 'cdh-bundle-card',
          itemCount: items.length,
          sections: hata ? undefined : veri.sections,
          didError: hata,
        },
      });

      if (hata) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: veri?.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: veri?.description, errors: veri?.errors },
          })
        );
        if (durum) durum.textContent = veri?.description || buton.dataset.hata || '';
        buton.textContent = buton.dataset.hata || metin;
      } else {
        if (durum) durum.textContent = buton.dataset.eklendi || '';
        buton.textContent = buton.dataset.eklendi || metin;
        buton.classList.add('is-eklendi');
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      ertelenen.reject(error);
      if (durum) durum.textContent = buton.dataset.hata || '';
      buton.textContent = buton.dataset.hata || metin;
    } finally {
      buton.removeAttribute('aria-busy');
      window.setTimeout(() => {
        if (!buton.isConnected) return;
        buton.disabled = false;
        buton.textContent = metin;
        buton.classList.remove('is-eklendi');
      }, 2600);
    }
  }
}

if (!customElements.get('cdh-tabbed-shelf')) customElements.define('cdh-tabbed-shelf', CdhTabbedShelf);
if (!customElements.get('cdh-scroller')) customElements.define('cdh-scroller', CdhScroller);
if (!customElements.get('cdh-bundle-card')) customElements.define('cdh-bundle-card', CdhBundleCard);

/* Mağaza saatleri: bugünün satırını vurgula (İstanbul saati; sayfa önbelleğinden bağımsız) */
const GUNLER = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

function bugunuVurgula(kok = document) {
  let bugun;
  try {
    const kisa = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'Europe/Istanbul' }).format(new Date());
    bugun = GUNLER[/** @type {keyof typeof GUNLER} */ (kisa)];
  } catch {
    bugun = new Date().getDay();
  }
  if (bugun === undefined) return;
  for (const hucre of kok.querySelectorAll('[data-cdh-saatler] [data-gunler]')) {
    if (!(hucre instanceof HTMLElement)) continue;
    const gunler = (hucre.dataset.gunler || '')
      .split(',')
      .map((g) => Number(g.trim()))
      .filter((g) => !Number.isNaN(g));
    hucre.classList.toggle('is-bugun', gunler.includes(bugun));
  }
}

bugunuVurgula();
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) bugunuVurgula(event.target);
});
